<script setup>
import { computed } from 'vue'

const props = defineProps({
  item: {
    type: Object,
    required: true
  }
})

const emit = defineEmits(['select'])

const preview = computed(() => props.item._upgradePreviewData)
const currency = computed(() => preview.value?.currency_code || 'EUR')

const formatCurrency = (amount) => {
  if (!amount) return '0,00'
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: currency.value
  }).format(parseFloat(amount) / 100)
}

const formatDate = (dateString) => {
  if (!dateString) return ''
  return new Intl.DateTimeFormat('it-IT', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  }).format(new Date(dateString))
}

const dueToday = computed(() => formatCurrency(preview.value?.update_summary?.result?.amount))
const proratedCharge = computed(() => formatCurrency(preview.value?.update_summary?.charge?.amount))
const unusedCredit = computed(() => formatCurrency(preview.value?.update_summary?.credit?.amount))
const immediateTax = computed(() => formatCurrency(preview.value?.immediate_transaction?.details?.totals?.tax))

const nextRenewalTotal = computed(() => formatCurrency(preview.value?.next_transaction?.details?.totals?.total))
const nextRenewalDate = computed(() => formatDate(preview.value?.next_transaction?.billing_period?.starts_at))

const handleSelect = () => {
  emit('select', props.item.price.id, props.item._discountId || null)
}
</script>

<template>
  <q-card>
    <q-card-section class="row items-center justify-between no-wrap">
      <div class="text-h6 text-weight-bold text-ellipsis">
        {{ item.product?.name }}
      </div>
    </q-card-section>

    <q-card-section>
      <div class="text-body2 text-grey-8">
        {{ $t(item.product.description || 'subscriptions.noDescription') }}
      </div>

      <q-list
        dense
      >
        <q-item-label
          header
          class="text-weight-bold text-primary text-uppercase"
        >
          {{ $t('subscriptions.dueToday') }}
        </q-item-label>

        <q-item>
          <q-item-section class="text-body2 text-grey-7">
            {{ $t('subscriptions.newPlanProrated') }}
          </q-item-section>
          <q-item-section
            side
            class="text-body2"
          >
            {{ proratedCharge }}
          </q-item-section>
        </q-item>

        <q-item v-if="parseFloat(preview?.update_summary?.credit?.amount || 0) < 0">
          <q-item-section class="text-body2 text-positive">
            {{ $t('subscriptions.unusedCredit') }}
          </q-item-section>
          <q-item-section
            side
            class="text-body2 text-positive text-weight-bold"
          >
            {{ unusedCredit }}
          </q-item-section>
        </q-item>

        <q-item v-if="parseFloat(preview?.immediate_transaction?.details?.totals?.tax || 0) > 0">
          <q-item-section class="text-body2 text-grey-7">
            {{ $t('subscriptions.taxRate') }}
          </q-item-section>
          <q-item-section
            side
            class="text-body2"
          >
            {{ immediateTax }}
          </q-item-section>
        </q-item>

        <q-separator class="q-my-sm" />

        <q-item>
          <q-item-section>
            <div class="text-subtitle1 text-weight-bolder text-grey-9">
              {{ $t('subscriptions.total') }}
            </div>
          </q-item-section>
          <q-item-section
            side
            class="text-h5 text-weight-bolder text-primary"
          >
            {{ dueToday }}
          </q-item-section>
        </q-item>
      </q-list>

      <q-list
        dense
        class="q-mt-md"
      >
        <q-item>
          <q-item-section>
            <q-item-label class="text-caption text-grey-7">
              {{ $t('subscriptions.manage.nextBilling') }} {{ nextRenewalDate }}
            </q-item-label>
            <q-item-label class="text-body2 text-weight-medium">
              {{ $t('subscriptions.recurringTotal') }}
            </q-item-label>
          </q-item-section>
          <q-item-section
            side
            class="text-body1 text-weight-bold"
          >
            {{ nextRenewalTotal }}
          </q-item-section>
        </q-item>
      </q-list>
    </q-card-section>

    <q-card-actions
      align="center"
      class="q-pb-md"
    >
      <q-btn
        color="primary"
        unelevated
        class="text-weight-bold full-width"
        :label="$t('common.confirm')"
        @click.stop="handleSelect"
      />
    </q-card-actions>
  </q-card>
</template>

